import type { IRegDataStagedChange } from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { isStagedChangeCompleteForBulkSync } from "./regDataAlignmentDisplay";
import type { ReconciliationGridRow } from "./reconciliationGridTypes";
import { ReconciliationActionIcons } from "./reconciliationGridTypes";
import { RowVisualStates, resolveRowVisualState } from "./reconciliationRowVisualState";
import { stageFromReconciliationRow, stageSoftDeleteFromGridRow } from "./regDataAlignmentState";

export interface BulkActionCounts {
    syncAll: number;
    revertSync: number;
    softDeleteAll: number;
    restoreDelete: number;
}

export interface BulkStageSyncResult {
    changes: IRegDataStagedChange[];
    stagedCount: number;
    skippedIncompleteCount: number;
}

export function isUnstagedArrowRow(row: ReconciliationGridRow): boolean {
    return (
        resolveRowVisualState(row) === RowVisualStates.Unstaged &&
        row.actionIcon === ReconciliationActionIcons.Stage &&
        !row.actionDisabled
    );
}

export function isStagedSyncRow(row: ReconciliationGridRow): boolean {
    return resolveRowVisualState(row) === RowVisualStates.StagedSync;
}

export function isUnstagedBinRow(row: ReconciliationGridRow): boolean {
    return (
        resolveRowVisualState(row) === RowVisualStates.Unstaged &&
        row.actionIcon === ReconciliationActionIcons.SoftDelete &&
        !row.actionDisabled
    );
}

export function isStagedDeleteRow(row: ReconciliationGridRow): boolean {
    return resolveRowVisualState(row) === RowVisualStates.StagedDelete;
}

export function countBulkActionTargets(rows: ReconciliationGridRow[]): BulkActionCounts {
    let syncAll = 0;
    let revertSync = 0;
    let softDeleteAll = 0;
    let restoreDelete = 0;

    for (const row of rows) {
        if (isUnstagedArrowRow(row)) syncAll++;
        else if (isStagedSyncRow(row)) revertSync++;
        else if (isUnstagedBinRow(row)) softDeleteAll++;
        else if (isStagedDeleteRow(row)) restoreDelete++;
    }

    return { syncAll, revertSync, softDeleteAll, restoreDelete };
}

export function filterUnstagedArrowRows(rows: ReconciliationGridRow[]): ReconciliationGridRow[] {
    return rows.filter(isUnstagedArrowRow);
}

export function filterStagedSyncRows(rows: ReconciliationGridRow[]): ReconciliationGridRow[] {
    return rows.filter(isStagedSyncRow);
}

export function filterUnstagedBinRows(rows: ReconciliationGridRow[]): ReconciliationGridRow[] {
    return rows.filter(isUnstagedBinRow);
}

export function filterStagedDeleteRows(rows: ReconciliationGridRow[]): ReconciliationGridRow[] {
    return rows.filter(isStagedDeleteRow);
}

export function buildBulkStageSyncChanges(rows: ReconciliationGridRow[]): BulkStageSyncResult {
    const changes: IRegDataStagedChange[] = [];
    let skippedIncompleteCount = 0;

    for (const row of filterUnstagedArrowRows(rows)) {
        const change = stageFromReconciliationRow(row);
        if (!change) continue;

        if (!isStagedChangeCompleteForBulkSync(change)) {
            skippedIncompleteCount++;
            continue;
        }

        changes.push(change);
    }

    return {
        changes,
        stagedCount: changes.length,
        skippedIncompleteCount,
    };
}

export function buildBulkSoftDeleteChanges(rows: ReconciliationGridRow[]): IRegDataStagedChange[] {
    const changes: IRegDataStagedChange[] = [];
    for (const row of filterUnstagedBinRows(rows)) {
        const change = stageSoftDeleteFromGridRow(row);
        if (change) changes.push(change);
    }
    return changes;
}

export function collectStagedSyncChangeKeys(rows: ReconciliationGridRow[]): string[] {
    return filterStagedSyncRows(rows)
        .map((row) => row.stagedChangeKey)
        .filter((key): key is string => !!key);
}

export function collectStagedDeleteChangeKeys(rows: ReconciliationGridRow[]): string[] {
    return filterStagedDeleteRows(rows)
        .map((row) => row.stagedChangeKey)
        .filter((key): key is string => !!key);
}
